import React, { useState, useRef } from 'react';
import { Modal } from '../ui/Modal';
import { Upload, Plus, Loader2, Link as LinkIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface PostOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface OpportunityForm {
  title: string;
  description: string;
  type: string;
  value: string;
  deadline: string;
  requirements: string;
  naicsCode: string;
  setAside: string;
  agency: string;
  location: string;
  links: Array<{ url: string; description: string }>;
  attachments: File[];
}

const initialForm: OpportunityForm = {
  title: '',
  description: '',
  type: '',
  value: '',
  deadline: '',
  requirements: '',
  naicsCode: '',
  setAside: '',
  agency: '',
  location: '',
  links: [],
  attachments: [],
};

export function PostOpportunityModal({ isOpen, onClose }: PostOpportunityModalProps) {
  const [form, setForm] = useState<OpportunityForm>(initialForm);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newLink, setNewLink] = useState({ url: '', description: '' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // API call would go here to save the opportunity
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('Opportunity posted successfully');
      onClose();
      setForm(initialForm);
    } catch (error) {
      console.error('Failed to post opportunity:', error);
      toast.error('Failed to post opportunity');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files],
    }));
  };

  const removeFile = (index: number) => {
    setForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const addLink = () => {
    if (!newLink.url || !newLink.description) {
      toast.error('Please provide both URL and description');
      return;
    }

    setForm(prev => ({
      ...prev,
      links: [...prev.links, newLink],
    }));
    setNewLink({ url: '', description: '' });
  };

  const removeLink = (index: number) => {
    setForm(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Post New Opportunity">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Opportunity Title
            </label>
            <input
              type="text"
              id="title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              required
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
              Opportunity Type
            </label>
            <select
              id="type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              required
            >
              <option value="">Select type</option>
              <option value="contract">Contract</option>
              <option value="subcontract">Subcontract</option>
              <option value="partnership">Partnership</option>
              <option value="grant">Grant</option>
            </select>
          </div>

          <div>
            <label htmlFor="value" className="block text-sm font-medium text-gray-700">
              Estimated Value
            </label>
            <input
              type="number"
              id="value"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label htmlFor="deadline" className="block text-sm font-medium text-gray-700">
              Submission Deadline
            </label>
            <input
              type="date"
              id="deadline"
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label htmlFor="naicsCode" className="block text-sm font-medium text-gray-700">
              NAICS Code
            </label>
            <input
              type="text"
              id="naicsCode"
              value={form.naicsCode}
              onChange={(e) => setForm({ ...form, naicsCode: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
            />
          </div>

          <div>
            <label htmlFor="setAside" className="block text-sm font-medium text-gray-700">
              Set-Aside
            </label>
            <select
              id="setAside"
              value={form.setAside}
              onChange={(e) => setForm({ ...form, setAside: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
            >
              <option value="">None</option>
              <option value="8(a)">8(a)</option>
              <option value="HUBZone">HUBZone</option>
              <option value="SDVOSB">SDVOSB</option>
              <option value="WOSB">WOSB</option>
            </select>
          </div>

          <div>
            <label htmlFor="agency" className="block text-sm font-medium text-gray-700">
              Agency
            </label>
            <input
              type="text"
              id="agency"
              value={form.agency}
              onChange={(e) => setForm({ ...form, agency: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
            />
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700">
              Location
            </label>
            <input
              type="text"
              id="location"
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
            />
          </div>

          <div className="sm:col-span-2">
            <label htmlFor="requirements" className="block text-sm font-medium text-gray-700">
              Requirements
            </label>
            <textarea
              id="requirements"
              value={form.requirements}
              onChange={(e) => setForm({ ...form, requirements: e.target.value })}
              rows={4}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
            />
          </div>

          {/* Related Links */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Related Links
            </label>
            <div className="mt-2 space-y-3">
              <div className="flex space-x-2">
                <input
                  type="url"
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="Enter URL"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
                />
                <input
                  type="text"
                  value={newLink.description}
                  onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                  placeholder="Link description"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-purple-500 focus:outline-none focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={addLink}
                  className="inline-flex items-center rounded-md bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              {form.links.map((link, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <LinkIcon className="h-4 w-4 text-gray-400" />
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-purple-600 hover:text-purple-500"
                  >
                    {link.description}
                  </a>
                  <button
                    type="button"
                    onClick={() => removeLink(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* File Attachments */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700">
              Attachments
            </label>
            <div className="mt-1">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                multiple
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full items-center justify-center rounded-md border-2 border-dashed border-gray-300 px-6 py-4 hover:border-purple-500"
              >
                <Upload className="mr-2 h-5 w-5 text-gray-400" />
                <span>Upload files</span>
              </button>
            </div>

            {form.attachments.length > 0 && (
              <ul className="mt-2 divide-y divide-gray-200">
                {form.attachments.map((file, index) => (
                  <li key={index} className="flex items-center justify-between py-2">
                    <div className="flex items-center">
                      <Upload className="mr-2 h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-700">{file.name}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Posting...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Post Opportunity
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}